import { getTimezoneOffset } from 'date-fns-tz';
import { DATE_TIME_LOCALE } from './constants.js';

/**
 * Normalizes some inconsistencies in our allowed internal time zone values to ensure they will
 * work properly when using date-related APIs. Will return default of "local" if time zone
 * not specified.
 */ function normalizeTimeZone(timeZone = 'local') {
    return timeZone === 'utc' ? 'UTC' : timeZone;
}
/**
 * Normalizes our allowed internal time zone values into an IANA time zone value, which is necessary
 * for some time zone formatting utilities.
 */ function normalizeIANATimeZone(timeZone = 'local') {
    const normalizedTimeZone = normalizeTimeZone(timeZone);
    if (isLocalTimeZone(normalizedTimeZone)) {
        return getLocalIANATimeZone();
    }
    return normalizedTimeZone;
}
/**
 * Returns true if the specified time zone value indicates it is using the user's
 * local, browser-based time zone. If not specified, the time zone is assumed to be
 * local.
 */ function isLocalTimeZone(timeZone) {
    const lowerTimeZone = normalizeTimeZone(timeZone).toLowerCase();
    return lowerTimeZone === 'local' || lowerTimeZone === 'browser';
}
/**
 * Get the local browser time zone in IANA format (e.g. America/New_York).
 */ function getLocalIANATimeZone() {
    return Intl.DateTimeFormat().resolvedOptions().timeZone;
}
/**
 * Returns the time zone offset from UTC in milliseconds.
 */ function getTimeZoneOffsetMs(timeZone) {
    const normalizedTimeZone = normalizeIANATimeZone(timeZone);
    const now = new Date();
    return getTimezoneOffset(normalizedTimeZone, now);
}
/**
 * Returns the name of the time zone.
 */ function getTimeZoneName(timeZone, { variant = 'short' } = {}) {
    const normalizedTimeZone = normalizeIANATimeZone(timeZone);
    const parts = Intl.DateTimeFormat(DATE_TIME_LOCALE, {
        timeZone: normalizedTimeZone,
        timeZoneName: variant
    }).formatToParts(new Date());
    return parts.find((part)=>part.type === 'timeZoneName')?.value;
}
// Looks for the digits of an offset.
const TZ_OFFSET_REGEX = /([+-]?)(\d{1,2})(:\d{2})?/;
/**
 * Attempts to parse a string-based long time zone offset (e.g. +08:00, +00:30) from a time zone
 * name. Returns undefined if not able to determine an offset. Useful in some rare parsing cases
 * where we need to do some date math based on a string-based offset name.
 *
 * Intentionally organizing into the long format, which is compatible with the date-fns-tz lib we
 * use for time zone related utilities.
 */ function getOffsetStrFromTimeZoneName(timeZoneStr) {
    if (!timeZoneStr) {
        return;
    }
    if (timeZoneStr === 'GMT' || timeZoneStr === 'UTC') {
        return 'UTC';
    }
    // If we can parse a likely offset from the string, return it as is.
    const offsetMatch = timeZoneStr.match(TZ_OFFSET_REGEX);
    if (offsetMatch && offsetMatch[2]) {
        const [_, signStr, hourStr, minuteStr] = offsetMatch;
        const sign = signStr || '';
        // Ensure hour is two digits.
        const hour = hourStr.padStart(2, '0');
        // Fall back to :00 if not specified.
        const minute = minuteStr || ':00';
        return `${sign}${hour}${minute}`;
    }
    // If offset not found, attempt to pull from the Intl API. Useful for covering the 3-letter
    // time zone abbreviations like PST.
    try {
        const now = new Date();
        const timeZoneParts = Intl.DateTimeFormat(DATE_TIME_LOCALE, {
            timeZone: timeZoneStr.replace('GMT', ''),
            timeZoneName: 'longOffset'
        }).formatToParts(now);
        const timeZoneOffset = timeZoneParts.find((part)=>part.type === 'timeZoneName')?.value;
        if (timeZoneOffset === 'GMT') {
            return 'UTC';
        }
        if (timeZoneOffset) {
            // Remove GMT prefix to just leave behind the offset.
            return timeZoneOffset.replace('GMT', '');
        }
    } catch (e) {
    // Do nothing. Invalid time zone.
    }
}

export { getLocalIANATimeZone, getOffsetStrFromTimeZoneName, getTimeZoneName, getTimeZoneOffsetMs, isLocalTimeZone, normalizeIANATimeZone, normalizeTimeZone };
//# sourceMappingURL=time-zone-utils.js.map
